import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, MapPin, Mail } from "lucide-react";
import { toast } from "sonner";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/shared/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

// Waitlist page — shown when the Quick Quote ZIP module on the home page
// detects that the entered ZIP belongs to a state we don't yet cover.
// The path encodes the resolved {zip, city, state} so we can pre-fill the
// form, but the page must also work when the user lands on it directly
// (e.g., shared link) with no query params.
export default function WaitlistPage() {
  const { t, locale } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const L = (en, es) => (locale === "es" ? es : en);

  const initial = {
    full_name: "",
    email: "",
    phone: "",
    zip: params.get("zip") || "",
    city: params.get("city") || "",
    state: params.get("state") || "",
  };
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const hero = form.city
    ? L(`We're not in ${form.city}${form.state ? `, ${form.state}` : ""} yet — but we'd love to be!`,
        `Todavía no llegamos a ${form.city}${form.state ? `, ${form.state}` : ""} — ¡pero queremos hacerlo!`)
    : L("We'd love to clean for you — let's expand together!",
        "Nos encantaría limpiar para ti — ¡crezcamos juntos!");

  const submit = async () => {
    if (!form.full_name.trim()) { toast.error(L("Full name is required", "El nombre completo es obligatorio")); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { toast.error(L("Enter a valid email", "Ingresa un email válido")); return; }
    if (!form.phone.trim()) { toast.error(L("Phone is required", "El teléfono es obligatorio")); return; }
    if (!form.city.trim()) { toast.error(L("City is required", "La ciudad es obligatoria")); return; }
    setSubmitting(true);
    try {
      await api.post("/waitlist", {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        zip: form.zip.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        country: "US",
        locale,
        source: "quick_quote_no_coverage",
      });
      setSuccess(true);
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || L("Something went wrong. Please try again.", "Algo salió mal. Intenta de nuevo.");
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="waitlist-page">
      <SEO
        title={L("Join the Ever Clean Home waitlist", "Súmate a la lista de espera de Ever Clean Home")}
        description={L("Leave your details and we'll let you know the moment Ever Clean Home expands to your area.",
                       "Deja tus datos y te avisamos apenas Ever Clean Home llegue a tu zona.")}
      />
      <Header />
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-12 md:py-20">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-[#4B5563] hover:text-[#0EA5A4] mb-6" data-testid="waitlist-back-link">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> {L("Back to home", "Volver al inicio")}
        </Link>

        {success ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="text-center"
            data-testid="waitlist-success"
          >
            <div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-50 grid place-items-center mb-5">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter mb-4">
              {L("Thanks! You're on the list.", "¡Listo! Estás en la lista.")}
            </h1>
            <p className="text-[#4B5563] text-base md:text-lg max-w-md mx-auto leading-relaxed">
              {L(`We'll write you the moment we're in ${form.city || "your city"}.`,
                 `Te escribimos apenas estemos en ${form.city || "tu ciudad"}.`)}
            </p>
            <Button onClick={() => navigate("/")} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white mt-8 h-12 px-7" data-testid="waitlist-success-back">
              {L("Back to home", "Volver al inicio")}
            </Button>
          </motion.div>
        ) : (
          <>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
              <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter leading-[1.05]" data-testid="waitlist-hero">
                {hero}
              </h1>
              <p className="text-[#4B5563] mt-4 text-base md:text-lg max-w-xl" data-testid="waitlist-subtitle">
                {L("Leave your details and we'll let you know the moment we expand to your area.",
                   "Deja tus datos y te avisamos apenas estemos disponibles en tu zona.")}
              </p>
            </motion.div>

            <Card className="rounded-2xl p-6 md:p-8 border border-[#E5E7EB] mt-8" data-testid="waitlist-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={L("Full name", "Nombre completo")} required>
                  <Input value={form.full_name} onChange={(e) => set({ full_name: e.target.value })}
                    placeholder={L("Maria Lopez", "María Lopez")}
                    className="h-12 rounded-xl" data-testid="waitlist-full-name" />
                </Field>
                <Field label={L("Email", "Email")} required>
                  <Input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })}
                    placeholder="you@example.com"
                    className="h-12 rounded-xl" data-testid="waitlist-email" />
                </Field>
                <Field label={L("Phone", "Teléfono")} required>
                  <Input type="tel" value={form.phone} onChange={(e) => set({ phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                    className="h-12 rounded-xl" data-testid="waitlist-phone" />
                </Field>
                <Field label={L("ZIP code", "Código postal")}>
                  <Input value={form.zip}
                    onChange={(e) => set({ zip: e.target.value.replace(/[^0-9]/g, "").slice(0, 5) })}
                    inputMode="numeric"
                    placeholder="33139"
                    className="h-12 rounded-xl font-mono tracking-wider" data-testid="waitlist-zip" />
                </Field>
                <Field label={L("City", "Ciudad")} required>
                  <Input value={form.city} onChange={(e) => set({ city: e.target.value })}
                    placeholder="Miami Beach"
                    className="h-12 rounded-xl" data-testid="waitlist-city" />
                </Field>
                <Field label={L("State", "Estado")}>
                  <Input value={form.state} onChange={(e) => set({ state: e.target.value.toUpperCase().slice(0, 2) })}
                    placeholder="FL"
                    className="h-12 rounded-xl font-mono uppercase" data-testid="waitlist-state" />
                </Field>
              </div>

              <Button
                onClick={submit}
                disabled={submitting}
                className="mt-6 w-full h-14 rounded-2xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-base font-semibold shadow-lg shadow-[#0EA5A4]/20 hover:scale-[1.01] transition-transform"
                data-testid="waitlist-submit"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>
                  <Mail className="w-4 h-4 mr-2" /> {L("Join the waitlist", "Sumarme a la lista")}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>}
              </Button>
              <p className="text-xs text-[#9CA3AF] mt-3 text-center flex items-center justify-center gap-1.5" data-testid="waitlist-microcopy">
                <MapPin className="w-3.5 h-3.5" />
                {L("We'll never spam you or share your info.", "Nunca te vamos a spamear ni compartir tu info.")}
              </p>
            </Card>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-sm font-medium text-[#4B5563]">{label}{required && " *"}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
